import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { AuthService, User } from '@auth0/auth0-angular';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.css'
})
export class ShellComponent implements OnInit {
  private doc = inject(DOCUMENT);
  private auth = inject(AuthService);
  private router = inject(Router);

  user$: Observable<User | null | undefined> = this.auth.user$;
  isHomePage = true;

  ngOnInit(): void {
    this.checkIsHomePage(this.router.url);

    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(e => {
      this.checkIsHomePage(e.urlAfterRedirects || e.url);
    });

    this.user$.pipe(
      tap(u => {
        if (u) {
          console.log('[Auth0 User Claims]', u);
        }
      })
    ).subscribe();
  }

  private checkIsHomePage(url: string): void {
    const cleanUrl = (url || '').split('?')[0].split('#')[0];
    this.isHomePage = cleanUrl === '/' || cleanUrl === '';
  }

  getUserDisplayName(user: User): string {
    // 1. Prénom et Nom de l'utilisateur
    if (user.given_name || user.family_name) {
      return `${user.given_name || ''} ${user.family_name || ''}`.trim();
    }
    // 2. Nom complet s'il ne s'agit pas d'un email
    if (user.name && !user.name.includes('@')) {
      return user.name;
    }
    // 3. Formattage depuis l'email (ex: marion.boue-bigne@terega.fr -> Marion Boue-Bigne)
    const emailStr = user.email || user.name || '';
    if (emailStr.includes('@')) {
      const username = emailStr.split('@')[0];
      const parts = username.split(/[._-]/);
      return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' ');
    }
    return user.name || 'Utilisateur';
  }

  getUserProfile(user: User): string {
    if (!user) return 'Utilisateur';

    // Recherche exhaustive des clés de rôles / profil Auth0
    const rawRoles = user['https://terega.fr/roles']
      || user['https://terega.fr/role']
      || user['https://api.terega.fr/roles']
      || user['https://api.terega.fr/role']
      || user['https://terega.eu.auth0.com/roles']
      || user['roles']
      || user['role']
      || user['user_metadata']?.role
      || user['user_metadata']?.roles
      || user['user_metadata']?.profil
      || user['app_metadata']?.role
      || user['app_metadata']?.roles
      || user['app_metadata']?.profil
      || user['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
      || user['profil']
      || user['profile'];

    let foundRole = '';

    if (Array.isArray(rawRoles) && rawRoles.length > 0) {
      foundRole = String(rawRoles[0]);
    } else if (typeof rawRoles === 'string' && rawRoles.trim() !== '') {
      foundRole = rawRoles;
    }

    if (foundRole) {
      const lower = foundRole.toLowerCase();
      if (lower.includes('admin')) return 'Administrateur';
      if (lower.includes('notaire')) return 'Notaire';
      if (lower.includes('prestataire')) return 'Prestataire';
      if (lower.includes('agent')) return 'Agent Teréga';
      return foundRole.charAt(0).toUpperCase() + foundRole.slice(1);
    }

    // Par défaut, si aucun rôle n'est explicitement trouvé dans les claims JWT
    return 'Administrateur';
  }

  logout(): void {
    this.auth.logout({
      logoutParams: { returnTo: this.doc.location.origin + '/login' }
    });
  }
}
